<template>
  <PbDialog
    :state="state"
    :dialog-title="translate('pb2.colorDialog.title')"
    size="xlarge"
    :horizontal-scroll="false"
    class-name="pb-color-dialog"
    @close="cancel"
  >
    <template #afterTitle>
      <div class="pb-color-dialog-header">
        <ColorPreview
          class="pb-color-dialog-header-color-preview"
          :value="color"
          styled
          size="large"
        />
        <ControlButton
          class="pb-color-dialog-header-add-favorite"
          :tooltip="translate('pb2.colorDialog.addFavoriteTooltip')"
          :label="translate('pb2.colorDialog.addFavoriteLabel')"
          :disabled="isFavorite"
          @click="appendFavorite(color)"
        />
        <ControlButton
          class="pb-color-dialog-header-mode-selector"
          :tooltip="mode === MODE_PALETTE ? translate('pb2.colorDialog.customMode') : translate('pb2.colorDialog.paletteMode')"
          :icon="mode === MODE_PALETTE ? 'params' : 'apps'"
          @click="mode = (mode === MODE_PALETTE ? MODE_CUSTOM : MODE_PALETTE)"
        />

        <ControlButton
          class="pb-color-dialog-header-save btn-success"
          :tooltip="translate('pb2.colorDialog.saveTooltip')"
          icon="save"
          @click="save"
        />
      </div>
    </template>

    <template v-if="mode === MODE_CUSTOM">
      <ColorPicker
        v-model="color"
        :width="pickerWidth"
        :disabled="disabled"
      />
      <div class="pb-color-dialog-row">
        <div class="color-input">
          <PbInput
            v-model="slider"
            :type="INPUT_TYPE_SWITCH"
            :label="translate('pb2.colorDialog.slidersLabel')"
          />
        </div>
        <HexColorInput
          v-model="color"
          :disabled="disabled"
        />
      </div>
      <div class="pb-color-dialog-row">
        <RGBColorInput
          v-model="color"
          :slider="slider"
          :disabled="disabled"
        />
        <HSVColorInput
          v-model="color"
          :slider="slider"
          :disabled="disabled"
        />
      </div>
    </template>
    <template v-else>
      <div class="pb-color-dialog-palette-wrapper">
        <div class="pb-color-dialog-palette-header">
          <div class="pb-color-dialog-palette-title">
            {{ translate('pb2.colorDialog.favoritesPaletteTitle') }}
          </div>
          <div class="pb-color-dialog-palette-actions">
            <ControlButton
              v-if="isFavorite"
              :tooltip="translate('pb2.colorDialog.removeFavoriteTooltip')"
              :label="translate('pb2.colorDialog.removeFavoriteLabel')"
              class="btn-error"
              @click="removeFavorite(color)"
            />
            <ControlButton
              v-else
              :tooltip="translate('pb2.colorDialog.addFavoriteTooltip')"
              :label="translate('pb2.colorDialog.addFavoriteShortLabel')"
              @click="appendFavorite(color)"
            />
            <ControlButton
              class="btn-error pb-color-dialog-clear-favorites-button"
              :tooltip="translate('pb2.colorDialog.clearFavoritesTooltip')"
              :label="translate('pb2.colorDialog.clearFavoritesLabel')"
              @click="clearFavorites()"
            />
          </div>
        </div>
        <ColorPalette
          v-model="color"
          :colors="favoriteColors"
          :disabled="disabled"
          :placeholders="placeholdersCount"
        />
      </div>

      <div class="pb-color-dialog-palette-wrapper">
        <div class="pb-color-dialog-palette-header">
          <div class="pb-color-dialog-palette-title">
            {{ translate('pb2.colorDialog.recentlyUsedPaletteTitle') }}
          </div>
          <div class="pb-color-dialog-palette-actions">
            <ControlButton
              :tooltip="translate('pb2.colorDialog.clearRecentlyUsedTooltip')"
              :label="translate('pb2.colorDialog.clearRecentlyUsedLabel')"
              class="btn-error"
              @click="clearRecent"
            />
          </div>
        </div>
        <ColorPalette
          v-model="color"
          :colors="recentlyUsedColors"
          :disabled="disabled"
          :placeholders="placeholdersCount"
        />
      </div>

      <div class="pb-color-dialog-palette-wrapper">
        <div class="pb-color-dialog-palette-header">
          <div class="pb-color-dialog-palette-title">
            {{ translate('pb2.colorDialog.genericPaletteTitle') }}
          </div>
        </div>
        <ColorPalette
          v-model="color"
          :colors="colors"
          :disabled="disabled"
        />
      </div>
    </template>
  </PbDialog>
</template>

<script setup>
import { computed } from 'vue'

import { Color } from '@nicolaudie-group/ls-utilities'

import * as colorHelpers from '@nicolaudie-group/ng-ui-kit/helpers/color'

import useRefSync from '@nicolaudie-group/ng-ui-kit/composables/useRefSync'
import useMediaFallback from '@nicolaudie-group/ng-ui-kit/composables/useMediaFallback'

import ControlButton from '@nicolaudie-group/ng-ui-kit/components/ControlButton/ControlButton.vue'
import { INPUT_TYPE_SWITCH } from '@nicolaudie-group/ng-ui-kit/components/BaseInput/symbols'
import ColorPalette from '@nicolaudie-group/ng-ui-kit/components/Color/ColorPalette.vue'
import ColorPicker from '@nicolaudie-group/ng-ui-kit/components/Color/ColorPicker.vue'
import ColorPreview from '@nicolaudie-group/ng-ui-kit/components/Color/ColorPreview.vue'
import HSVColorInput from '@nicolaudie-group/ng-ui-kit/components/Color/HSVColorInput.vue'
import HexColorInput from '@nicolaudie-group/ng-ui-kit/components/Color/HexColorInput.vue'
import RGBColorInput from '@nicolaudie-group/ng-ui-kit/components/Color/RGBColorInput.vue'

import PbDialog from '@/components/PbDialog.vue'

import { useConstants } from '@/composables/useProfile'
import useRecentlyUsedColorList from '@/composables/useRecentlyUsedColorList'
import useFavoriteColorList from '@/composables/useFavoriteColorList'
import useColorpickerSliderToggle from '@/composables/useColorpickerSliderToggle'
import useColorpickerMode, { MODE_PALETTE, MODE_CUSTOM } from '@/composables/useColorpickerMode'
import useTranslate from '@/composables/useTranslate'
import PbInput from '@/components/PbInput.vue'

const props = defineProps({
  state: { type: Boolean, default: true },
  value: {
    type: [Color, String, Array, Number, Object],
    default: new Color([255, 255, 255, 1]),
  },
  storage: {
    type: String,
    default: 'default',
  },
  disabled: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['input', 'close'])

const { translate } = useTranslate()

const colorProp = computed(() => colorHelpers.parseColor(props.value))
const color = useRefSync(colorProp)

const BREAKPOINT_MEDIUM = '(min-width: 768px)'
const BREAKPOINT_LARGE = '(min-width: 992px)'
const BREAKPOINT_XLARGE = '(min-width: 1200px)'

const queries = [
  BREAKPOINT_XLARGE,
  BREAKPOINT_LARGE,
  BREAKPOINT_MEDIUM,
]

/*
Use the biggest multiple of 256 that fits in the dialog.
We use multiple of 256 to yield better resolution when converting between HSV and RGB.
The ideal width would be 1536 for 256 * 6 sections:
- dominant R
- dominant Y (R + G)
- dominant G
- dominant C (G + B)
- dominant B
- dominant M (B + R)
*/
const widths = [
  1024, // xlarge, higher priority over the following ones
  768, // large, higher priority over the following ones
  512, // medium, higher priority over the following ones
  256, // default, lowest priority when no other query was matched
]

const pickerWidth = useMediaFallback(widths, { queries })

/**
 * Use the maximum number of placeholders that can fit in only one line in the dialog
 */
const placeholdersCounts = [
  24, // xlarge, higher priority over the following ones
  19, // large, higher priority over the following ones
  14, // medium, higher priority over the following ones
  10, // default, lowest priority when no other query was matched
]

const placeholdersCount = useMediaFallback(placeholdersCounts, { queries })

const profileConstants = useConstants()

const colors = computed(() => (
  profileConstants.value.genericColors.map((item) => ({
    label: item.name,
    value: item.value,
  }))
))

const {
  list: recentlyUsedColors,
  prepend: prependRecent,
  clear: clearRecent,
} = useRecentlyUsedColorList()

const {
  list: favoriteColors,
  append: appendFavorite,
  remove: removeFavorite,
  clear: clearFavorites,
  has: hasFavorite,
} = useFavoriteColorList()

const isFavorite = computed(() => hasFavorite(color.value))

const slider = useColorpickerSliderToggle()

const mode = useColorpickerMode()

function cancel(event) {
  color.value = colorProp.value
  emit('close', event)
}

function save(event) {
  prependRecent(color.value)
  if (color.value.hex !== colorProp.value.hex) {
    emit('input', color.value)
  }
  emit('close', event)
}
</script>

<style lang="scss" src="./ColorDialog.scss" />
